import * as React from 'react'
import { ListFilterIcon } from 'lucide-react'

import { AuditAction } from '@/api/generated/api'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Command, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface EventTypeFilterProps {
  value: Set<AuditAction>
  onChange: (next: Set<AuditAction>) => void
  disabled?: boolean
}

interface ActionLabel {
  /** Standalone noun-phrase — filter checkboxes, trigger label. */
  filterLabel: string
  /** Verb-phrase embedded in a row sentence: "<actor> | <rowLabel> | <target>". */
  rowLabel: string
}

// Single source of truth for every event type's labels — CATEGORIES below only groups values,
// and index.tsx's row/CSV rendering reads rowLabel from here, so a label is never duplicated.
export const ACTION_LABELS: Record<AuditAction, ActionLabel> = {
  [AuditAction.MemberAdded]: { filterLabel: 'Member added', rowLabel: 'added member' },
  [AuditAction.MemberRemoved]: { filterLabel: 'Member removed', rowLabel: 'removed member' },
  [AuditAction.JoinRequestApproved]: {
    filterLabel: 'Join request approved',
    rowLabel: 'approved join request',
  },
  [AuditAction.JoinRequestRejected]: {
    filterLabel: 'Join request rejected',
    rowLabel: 'rejected join request',
  },
  [AuditAction.NotetakerConsentConfirmed]: {
    filterLabel: 'Notetaker consent confirmed',
    rowLabel: 'confirmed Notetaker consent',
  },
  [AuditAction.OrganizationSharingPolicyChanged]: {
    filterLabel: 'Sharing policy changed',
    rowLabel: 'changed sharing policy',
  },
  [AuditAction.OrganizationDataControlsChanged]: {
    filterLabel: 'Data controls changed',
    rowLabel: 'changed data controls',
  },
  [AuditAction.OrganizationNotetakerPolicyChanged]: {
    filterLabel: 'Notetaker policy changed',
    rowLabel: 'changed Notetaker policy',
  },
  [AuditAction.OrganizationNotetakerBlockedDomainsChanged]: {
    filterLabel: 'Notetaker blocked domains changed',
    rowLabel: 'changed Notetaker blocked domains',
  },
  [AuditAction.OrganizationAccessRestrictionChanged]: {
    filterLabel: 'Access restriction changed',
    rowLabel: 'changed access restriction',
  },
  [AuditAction.OrganizationProductAccessChanged]: {
    filterLabel: 'Product access changed',
    rowLabel: 'changed product access',
  },
  [AuditAction.OrganizationMembershipPolicyChanged]: {
    filterLabel: 'Membership policy changed',
    rowLabel: 'changed membership policy',
  },
  [AuditAction.OrganizationHipaaBaaSigned]: {
    filterLabel: 'HIPAA BAA signed',
    rowLabel: 'signed HIPAA BAA',
  },
  [AuditAction.OrganizationHipaaBaaRevoked]: {
    filterLabel: 'HIPAA BAA revoked',
    rowLabel: 'revoked HIPAA BAA',
  },
  [AuditAction.MemberHipaaBaaSigned]: {
    filterLabel: 'Personal HIPAA BAA signed',
    rowLabel: 'signed personal HIPAA BAA',
  },
  [AuditAction.MemberHipaaBaaRevoked]: {
    filterLabel: 'Personal HIPAA BAA revoked',
    rowLabel: 'revoked personal HIPAA BAA',
  },
  [AuditAction.NoteViewed]: { filterLabel: 'Note viewed', rowLabel: 'viewed note' },
  [AuditAction.NoteTranscriptViewed]: {
    filterLabel: 'Note transcript viewed',
    rowLabel: 'viewed note transcript',
  },
  [AuditAction.NoteShareVisibilityChanged]: {
    filterLabel: 'Note sharing changed',
    rowLabel: 'changed note sharing',
  },
  [AuditAction.NoteShareRecipientsAdded]: {
    filterLabel: 'Note recipient added',
    rowLabel: 'added note recipient',
  },
  [AuditAction.NoteShareRecipientRemoved]: {
    filterLabel: 'Note recipient removed',
    rowLabel: 'removed note recipient',
  },
  [AuditAction.FolderMemberAdded]: { filterLabel: 'Folder member added', rowLabel: 'added folder member' },
  [AuditAction.FolderMemberRemoved]: {
    filterLabel: 'Folder member removed',
    rowLabel: 'removed folder member',
  },
  [AuditAction.FolderMemberLeft]: { filterLabel: 'Folder member left', rowLabel: 'left folder' },
  [AuditAction.FolderNoteFiled]: { filterLabel: 'Note filed in folder', rowLabel: 'filed note' },
  [AuditAction.FolderNoteRemoved]: {
    filterLabel: 'Note removed from folder',
    rowLabel: 'removed note',
  },
  [AuditAction.FolderDeleted]: { filterLabel: 'Folder deleted', rowLabel: 'deleted folder' },
  [AuditAction.MemberRoleChanged]: { filterLabel: 'Member role changed', rowLabel: 'changed member role' },
  [AuditAction.MemberSeatChanged]: { filterLabel: 'Seat changed', rowLabel: 'changed member seat' },
  [AuditAction.MemberInvited]: { filterLabel: 'Member invited', rowLabel: 'invited member' },
  [AuditAction.MemberInvitationRevoked]: {
    filterLabel: 'Invitation revoked',
    rowLabel: 'revoked invitation',
  },
  [AuditAction.MemberInvitationResent]: { filterLabel: 'Invitation resent', rowLabel: 'resent invitation' },
  [AuditAction.OrganizationDomainAdded]: { filterLabel: 'Domain added', rowLabel: 'added domain' },
  [AuditAction.OrganizationDomainRemoved]: { filterLabel: 'Domain removed', rowLabel: 'removed domain' },
  [AuditAction.OrganizationSeatCommitmentChanged]: {
    filterLabel: 'Seat commitment changed',
    rowLabel: 'changed seat commitment',
  },
  [AuditAction.OrganizationSeatPreservationChanged]: {
    filterLabel: 'Seat preservation changed',
    rowLabel: 'changed seat preservation',
  },
  [AuditAction.OrganizationSeatCapChanged]: { filterLabel: 'Seat cap changed', rowLabel: 'changed seat cap' },
  [AuditAction.MemberEntitlementOverrideChanged]: {
    filterLabel: 'Entitlement override changed',
    rowLabel: 'changed entitlement override',
  },
  [AuditAction.OrganizationProductEntitlementGranted]: {
    filterLabel: 'Product entitlement granted',
    rowLabel: 'granted product entitlement',
  },
  [AuditAction.OrganizationGatesChanged]: {
    filterLabel: 'Gate changed',
    rowLabel: 'changed gate',
  },
  [AuditAction.DataExportCompleted]: {
    filterLabel: 'Data export completed',
    rowLabel: 'completed data export',
  },
  [AuditAction.DataExportFailed]: {
    filterLabel: 'Data export failed',
    rowLabel: 'failed data export',
  },
  [AuditAction.AccountDeletionRequested]: {
    filterLabel: 'Account deletion requested',
    rowLabel: 'requested account deletion',
  },
  [AuditAction.AccountDeletionCompleted]: {
    filterLabel: 'Account deletion completed',
    rowLabel: 'completed account deletion',
  },
  [AuditAction.AccountDeletionFailed]: {
    filterLabel: 'Account deletion failed',
    rowLabel: 'failed account deletion',
  },
  [AuditAction.UsageDataErasureRequested]: {
    filterLabel: 'Usage data erasure requested',
    rowLabel: 'requested usage data erasure',
  },
  [AuditAction.UsageDataErasureCompleted]: {
    filterLabel: 'Usage data erasure completed',
    rowLabel: 'completed usage data erasure',
  },
  [AuditAction.UsageDataErasureFailed]: {
    filterLabel: 'Usage data erasure failed',
    rowLabel: 'failed usage data erasure',
  },
  [AuditAction.ResourceTrashed]: {
    filterLabel: 'Note or meeting trashed',
    rowLabel: 'trashed',
  },
  [AuditAction.TranscriptRetentionDeleted]: {
    filterLabel: 'Transcript deleted by retention policy',
    rowLabel: 'deleted transcript (retention policy)',
  },
}

export const ALL_EVENT_TYPES = Object.keys(ACTION_LABELS) as AuditAction[]

// Placeholder grouping — trivially renameable/re-groupable as the catalog grows (Unit 4).
export const CATEGORIES: { label: string; events: AuditAction[] }[] = [
  {
    label: 'Membership',
    events: [
      AuditAction.MemberAdded,
      AuditAction.MemberRemoved,
      AuditAction.MemberRoleChanged,
      AuditAction.MemberInvited,
      AuditAction.MemberInvitationRevoked,
      AuditAction.MemberInvitationResent,
    ],
  },
  {
    label: 'Join requests',
    events: [AuditAction.JoinRequestApproved, AuditAction.JoinRequestRejected],
  },
  {
    label: 'Notetaker',
    events: [AuditAction.NotetakerConsentConfirmed],
  },
  {
    label: 'Organization settings',
    events: [
      AuditAction.OrganizationSharingPolicyChanged,
      AuditAction.OrganizationDataControlsChanged,
      AuditAction.OrganizationNotetakerPolicyChanged,
      AuditAction.OrganizationNotetakerBlockedDomainsChanged,
      AuditAction.OrganizationAccessRestrictionChanged,
      AuditAction.OrganizationProductAccessChanged,
      AuditAction.OrganizationMembershipPolicyChanged,
      AuditAction.OrganizationDomainAdded,
      AuditAction.OrganizationDomainRemoved,
    ],
  },
  {
    label: 'Compliance',
    events: [
      AuditAction.OrganizationHipaaBaaSigned,
      AuditAction.OrganizationHipaaBaaRevoked,
      AuditAction.MemberHipaaBaaSigned,
      AuditAction.MemberHipaaBaaRevoked,
    ],
  },
  {
    label: 'Note access',
    events: [AuditAction.NoteViewed, AuditAction.NoteTranscriptViewed],
  },
  {
    label: 'Sharing',
    events: [
      AuditAction.NoteShareVisibilityChanged,
      AuditAction.NoteShareRecipientsAdded,
      AuditAction.NoteShareRecipientRemoved,
    ],
  },
  {
    label: 'Folders',
    events: [
      AuditAction.FolderMemberAdded,
      AuditAction.FolderMemberRemoved,
      AuditAction.FolderMemberLeft,
      AuditAction.FolderNoteFiled,
      AuditAction.FolderNoteRemoved,
      AuditAction.FolderDeleted,
    ],
  },
  {
    label: 'Seats & entitlements',
    events: [
      AuditAction.MemberSeatChanged,
      AuditAction.OrganizationSeatCommitmentChanged,
      AuditAction.OrganizationSeatPreservationChanged,
      AuditAction.OrganizationSeatCapChanged,
      AuditAction.MemberEntitlementOverrideChanged,
      AuditAction.OrganizationProductEntitlementGranted,
      AuditAction.OrganizationGatesChanged,
    ],
  },
  {
    label: 'Data lifecycle',
    events: [
      AuditAction.DataExportCompleted,
      AuditAction.DataExportFailed,
      AuditAction.AccountDeletionRequested,
      AuditAction.AccountDeletionCompleted,
      AuditAction.AccountDeletionFailed,
      AuditAction.UsageDataErasureRequested,
      AuditAction.UsageDataErasureCompleted,
      AuditAction.UsageDataErasureFailed,
      AuditAction.ResourceTrashed,
      AuditAction.TranscriptRetentionDeleted,
    ],
  },
]

const toggleInSet = (set: Set<AuditAction>, value: AuditAction): Set<AuditAction> => {
  const next = new Set(set)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return next
}

const EventTypeFilter = ({ value, onChange, disabled = false }: EventTypeFilterProps) => {
  const [open, setOpen] = React.useState(false)

  const triggerLabel = React.useMemo(() => {
    if (value.size === 0) return 'No event types selected'
    if (value.size === ALL_EVENT_TYPES.length) return 'Event types'
    if (value.size === 1) return ACTION_LABELS[Array.from(value)[0]]?.filterLabel ?? 'Event types'
    return `${value.size} selected`
  }, [value])

  const toggleEvent = (eventValue: AuditAction) => onChange(toggleInSet(value, eventValue))

  const toggleCategory = (categoryEvents: AuditAction[]) => {
    const allSelected = categoryEvents.every((e) => value.has(e))
    const next = new Set(value)
    if (allSelected) {
      for (const e of categoryEvents) next.delete(e)
    } else {
      for (const e of categoryEvents) next.add(e)
    }
    onChange(next)
  }

  const allSelected = value.size === ALL_EVENT_TYPES.length
  const toggleAll = () => onChange(allSelected ? new Set() : new Set(ALL_EVENT_TYPES))

  return (
    <Popover open={open} onOpenChange={(next) => setOpen(disabled ? false : next)}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled} className="font-normal">
          <ListFilterIcon className="size-4 opacity-70" />
          <span className="max-w-[160px] truncate text-left">{triggerLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command shouldFilter={false}>
          <button
            type="button"
            onClick={toggleAll}
            className="hover:bg-accent flex w-full items-center border-b px-3 py-2.5 text-left text-sm font-semibold"
          >
            {allSelected ? 'Clear all' : 'Select all'}
          </button>
          <CommandList className="max-h-[420px]">
            <div className="py-1">
              {CATEGORIES.map(({ label, events }) => {
                const selectedCount = events.filter((e) => value.has(e)).length
                const categoryChecked = selectedCount === events.length
                const categoryIndeterminate = selectedCount > 0 && !categoryChecked

                return (
                  <div key={label}>
                    <label
                      className={cn(
                        'hover:bg-accent flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2.5 text-sm font-semibold',
                        categoryChecked && 'bg-muted'
                      )}
                    >
                      <span>{label}</span>
                      <Checkbox
                        checked={categoryIndeterminate ? 'indeterminate' : categoryChecked}
                        onCheckedChange={() => toggleCategory(events)}
                        aria-label={`Toggle ${label}`}
                      />
                    </label>
                    <div className="pb-1">
                      {events.map((event) => (
                        <label
                          key={event}
                          className="hover:bg-accent flex w-full cursor-pointer items-center justify-between gap-2 py-2 pl-9 pr-3 text-sm"
                        >
                          <span className="truncate">{ACTION_LABELS[event].filterLabel}</span>
                          <Checkbox
                            checked={value.has(event)}
                            onCheckedChange={() => toggleEvent(event)}
                            aria-label={`Toggle ${ACTION_LABELS[event].filterLabel}`}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default EventTypeFilter
